import React, { useState, useEffect } from 'react'
import { Box, Paper, Stack, Typography } from '@mui/material'
import { DragAndDropStack } from './drag-and-drop-stack'
import { type HttpResponse } from '../../../interfaces'
import { IconButtonWithTooltip } from 'components'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'

interface SortableListProps<T extends object> {
  title: string
  items: T[]
  id: string
  loading: boolean
  updateDisplayOrder?: (items: T[], itemSelected: T) => Promise<HttpResponse<T[]>>
  getCardText: (item: T) => string
  itemIdPrefix?: string
  isItemDisabled?: (item: T) => boolean
  onRetire?: (item: T) => void
  deferUpdates?: boolean
  onItemsChange?: (items: T[]) => void
}

export const SortableList = <T extends object>({
  title,
  items,
  id,
  loading,
  updateDisplayOrder,
  getCardText,
  itemIdPrefix,
  isItemDisabled,
  onRetire,
  deferUpdates = false,
  onItemsChange
}: SortableListProps<T>): JSX.Element => {
  const [internalItems, setInternalItems] = useState<T[]>(items)

  // Sync internal items when props items change
  useEffect(() => {
    setInternalItems(items)
  }, [items])

  // Internal function for deferred updates - just saves to state
  const handleDeferredUpdate = async (items: T[], _itemSelected: T): Promise<HttpResponse<T[]>> => {
    setInternalItems(items)
    onItemsChange?.(items)
    return {
      succeeded: true,
      result: items,
      errors: {},
      status: 200,
      message: null
    }
  }

  // Determine which function to pass to DragAndDropStack
  const effectiveUpdateDisplayOrder = deferUpdates
    ? handleDeferredUpdate
    : (updateDisplayOrder ?? handleDeferredUpdate)

  // Use internal items when deferring updates, otherwise use props items
  const effectiveItems = deferUpdates ? internalItems : items

  const getItemId = (prefix: string | undefined, item: T): string | undefined => {
    if (prefix === undefined || prefix === '') {
      return undefined
    }
    if (!('id' in item)) {
      return undefined
    }
    const itemId = (item as { id: number | string }).id
    if (itemId === undefined) {
      return undefined
    }
    return `${prefix}-${itemId}`
  }

  const renderCard = (item: T, index: number): React.ReactNode => {
    return (
      <Paper sx={{ p: 1 }} id={getItemId(itemIdPrefix, item)}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography>
            <Box component="span" sx={{
              mr: 2,
              fontWeight: 'bold',
              color: 'text.secondary',
              minWidth: '24px',
              display: 'inline-block'
            }}>
              {index + 1}.
            </Box>
            {getCardText(item)}
          </Typography>
          {onRetire != null && (
            <IconButtonWithTooltip
              label="Retire Member"
              color="secondary"
              onClick={() => { onRetire(item) }}
              icon={<HourglassEmptyIcon />}
            />
          )}
        </Box>
      </Paper>
    )
  }

  const shouldDisableDrag = (item: T): boolean => {
    if (loading || items.length <= 1) {
      return true
    }

    if (isItemDisabled !== undefined) {
      return isItemDisabled(item)
    }

    return false
  }

  const shouldBeGray = (item: T): boolean => {
    if (isItemDisabled !== undefined) {
      return isItemDisabled(item)
    }

    return false
  }

  return (
    <Stack spacing={1}>
      <Typography variant='h5'>{title}</Typography>
      <DragAndDropStack
        id={id}
        originalItems={effectiveItems}
        itemRenderer={renderCard}
        updateDisplayOrder={effectiveUpdateDisplayOrder}
        disableDrag={shouldDisableDrag}
        makeGray={shouldBeGray}
        suppressSuccessMessages={deferUpdates}
      />
    </Stack>
  )
}
